import { computed, makeAutoObservable, runInAction } from 'mobx';

import { Category, CategoryModel } from '../models/Category';
import { getCategories, updateCategory, addCategory, deleteCategory } from '../services/CategoriesAPI';
import { ReferenceData } from '../models/common';
import { errorStore } from './ErrorStore';
import { getActiveDepartmentCode } from '../utils/ActiveDepartment';

class CategoryStore {
	data: Category[] = [];
	isLoading = false;
	showModal = false;
	currentRow: CategoryModel | undefined;

	constructor() {
		makeAutoObservable(this);
	}

	async loadData() {
		if (this.data.length || this.isLoading) return;
		const departmentCode = getActiveDepartmentCode();

		try {
			this.setLoading(true);
			const categoriesResponse = await getCategories();
			if (departmentCode !== getActiveDepartmentCode()) {
				return;
			}

			this.setData(categoriesResponse || []);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async reloadForActiveDepartment() {
		this.reset();
		await this.loadData();
	}

	async delete(category: Category) {
		try {
			this.setLoading(true);
			await deleteCategory(category.id);
			this.setData(this.data.filter(c => c.id !== category.id));
			this.clearForm();
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async update(category: Category) {
		try {
			this.setLoading(true);
			await updateCategory(category);

			runInAction(() => {
				const index = this.data.findIndex(c => c.id === category.id);
				this.data[index] = category;
			});
			this.clearForm();
		} catch (error: any) {
			this.setError(error.message);
			throw error;
		} finally {
			this.setLoading(false);
		}
	}

	async addNew(newCategory: Category, onSuccess?: () => void) {
		try {
			this.setLoading(true);
			const addedCategory = await addCategory(newCategory);
			if (addedCategory) {
				this.setData([addedCategory, ...this.data]);
				this.clearForm();
				onSuccess?.();
			}
		} catch (error: any) {
			this.setError(error.message)
		} finally {
			this.setLoading(false);
		}
	}

	setupNew(name?: string) {
		this.setCurrentRow(new CategoryModel({ id: '', name: name || '', markup: 0 }));
		this.setShowModal(true);
	}

	getName(id?: string) {
		return this.data.find(a => a.id === id)?.name || 'No Category Found';
	}

	@computed
	get options() {
		return this.data as ReferenceData[];
	}

	get ITServicesExternalId() {
		return this.data.find(c => c.name === 'IT Services - External')?.id || '';
	}

	setLoading(loading: boolean) {
		this.isLoading = loading;
	}

	setError(error: string) {
		errorStore.report(error);
	}

	setShowModal(state: boolean) {
		this.showModal = state;
	}

	setCurrentRow(currentCategory: CategoryModel) {
		this.currentRow = currentCategory;
	}

	removeCurrentRow() {
		this.currentRow = undefined;
	}

	clearForm() {
		this.removeCurrentRow();
		this.setShowModal(false);
	}

	reset() {
		this.data = [];
		this.isLoading = false;
		this.clearForm();
	}

	setData(data: Category[]) {
		this.data = data;
	}
}

export const categoryStore = new CategoryStore()
